import i18next, { type ResourceKey } from "i18next";
import { createI18nLifecycle, hasUsableTranslations } from "./i18nLifecycle.js";
import type { ResolvedLocale } from "./locale.js";

// i18n runtime (#1088 Phase 1). react-i18next was the owner's decision; the two
// conditions attached to it were an npm audit report (clean, in the PR) and
// LAZY namespaces, so an English-only user never downloads the Spanish bundle.
//
// Namespace convention, one per feature slice:
//   apps/web/src/locales/<lng>/<namespace>.json
// A slice adds its two JSON files and calls useTranslation("<namespace>").
// Nothing else needs editing — this file must not grow a registry that every
// slice has to touch, or parallel translation work serialises on it.

// Vite turns this into one dynamic import per file, so each namespace is its
// own chunk and only the requested language is fetched.
const bundles = import.meta.glob("../locales/*/*.json");

function bundleKey(language: string, namespace: string): string {
  return `../locales/${language}/${namespace}.json`;
}

/**
 * A namespace that has no file resolves to an EMPTY bundle rather than an
 * error, so a language the app does not ship falls through to English instead
 * of crashing or painting raw keys.
 *
 * Every namespace is translated as of #1088, which means this branch is now
 * reached only by a NEW namespace whose Spanish file has not been written yet —
 * and it stays silent when that happens. `locales.test.ts` is what makes the
 * gap loud: it fails when the two languages ship different namespaces.
 */
const lifecycle = createI18nLifecycle(i18next, async (language, namespace) => {
  const load = bundles[bundleKey(language, namespace)];
  if (!load) return {};
  const loaded = await load() as { default?: ResourceKey } & ResourceKey;
  return loaded.default ?? loaded;
});

/**
 * Start the runtime, and RESOLVE when it can actually translate.
 *
 * The promise matters: `t()` before init returns the parseMissingKeyHandler
 * value — the bare last segment of the key — so a consumer that renders first
 * paints "now" and "brand" and then corrects itself. The caller waits on this
 * instead of rendering into that window.
 */
export function initI18n(locale: ResolvedLocale): Promise<void> {
  return lifecycle.ensure(locale);
}

export function hasUsableShellTranslations(locale: ResolvedLocale): boolean {
  return hasUsableTranslations(i18next, locale);
}

/**
 * A translated sentence for code that has no `t` to hand (#1088).
 *
 * Some display copy is produced too deep for a caller's `t`: the API layer's
 * error messages, the camera library's failures. Those modules are reached from
 * background queues and plain helpers as well as from components, so threading a
 * translation argument through every one of them would put i18n on layers that
 * should not know about it. They read the singleton instead.
 *
 * `english` is the FINISHED sentence, interpolations already substituted, and it
 * is returned whenever the bundle cannot answer — before init, or if the key is
 * ever dropped. `exists` is the guard rather than t()'s own `defaultValue`,
 * because this app's parseMissingKeyHandler takes precedence over defaultValue
 * and would paint the key path instead of the sentence.
 *
 * Only `common` is consulted: it is one of the two eagerly initialised
 * namespaces, so it is loaded before any of these callers can run.
 */
export function copy(key: string, english: string, values?: Record<string, unknown>): string {
  if (!i18next.isInitialized || !i18next.exists(key, { ns: "common" })) return english;
  // `replace` rather than spreading `values` into the options object: an
  // interpolation named like an i18next option (`count`, `context`, `lng`, ...)
  // would otherwise change how the key resolves instead of filling its
  // placeholder.
  return i18next.t(key, { ns: "common", ...(values ? { replace: values } : {}) });
}

export { i18next };
